import { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormikSelect from './FormikSelect';
import { createAdminUser, updateAdminUser } from '../services/project/userService';

// Validation schema for new user
const newUserValidationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  birthDate: Yup.date().required('Birthdate is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[\!@#\$%\^&\*\(\)\-_+=\[\]\{\}\|;:,.<>?]/, 'Password must contain at least one special character')
    .required('Password is required'),
  repeatPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Repeat Password is required'),
});

// Validation schema for existing user (no password validation)
const existingUserValidationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  birthDate: Yup.date().required('Birthdate is required'),
  password: Yup.string().notRequired(),  // Skip validation for existing users
  repeatPassword: Yup.string().notRequired(),  // Skip validation for existing users
});

const EditUserModal = ({ user, projects = [], store, show, onClose, refetch = () => {} }) => {
  const formik = useFormik({
    initialValues: {
      isEdit: !!user,  // Set to true if it's an edit, false if it's a new user
      firstName: user?.firstName || '',  // Always initialized
      lastName: user?.lastName || '',  // Always initialized
      username: user?.username || '',  // Always initialized
      birthDate: user?.birthDate || '',  // Always initialized
      projectId: user?.roles?.[0]?.projectId || '',  // Always initialized
      password: '',  // Always initialized for new users
      repeatPassword: '',  // Always initialized for new users
    },
    validationSchema: user ? existingUserValidationSchema : newUserValidationSchema,  // Choose schema based on user (edit or new)
    onSubmit: async ({ firstName, lastName, username, projectId, birthDate, password }) => {
      const date = new Date(birthDate);
      const formattedBirthDate = [
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
        date.getFullYear()
      ].join('/');

      const adminUserDetails = {
        firstName,
        lastName,
        username,
        birthDate: formattedBirthDate,
        projectId,
        password: password || undefined,  // Only include password if it's a new user
      };

      try {
        let response;
        if (user) {
          response = await updateAdminUser({ id: user.id, ...adminUserDetails });
        } else {
          response = await createAdminUser(adminUserDetails);
        }
        onClose();
        toast.success(response.message || `Admin user successfully ${user ? 'updated' : 'created' }!`);
        refetch();
      } catch (error) {
        console.error('Error creating admin user:', error);
        toast.error(error.response?.data?.errorMessage || 'Something went wrong');
      }
    },
  });

  // Auto-generate username if first/last name changes
  useEffect(() => {
    const username = `${formik.values.firstName}${formik.values.lastName}`.toLowerCase().replace(/\s+/g, '');
    formik.setFieldValue('username', username);
  }, [formik.values.firstName, formik.values.lastName]);

  // Pre-fill values when editing an existing user
  useEffect(() => {
    if (user) {
      const roleProjectId = user.roles?.[0]?.projectId ?? '';
      formik.setValues({
        isEdit: true,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        username: user.username || '',
        birthDate: user.birthDate ? new Date(user.birthDate).toISOString().split('T')[0] : '',
        projectId: roleProjectId,
        password: '',  // Do not pre-fill password
        repeatPassword: '',  // Do not pre-fill repeatPassword
      });
    }
  }, [user]);

  return (
    <Modal show={show} onHide={onClose} fullscreen="md-down" centered>
      <Modal.Header closeButton>
        <Modal.Title>{store ? store.name : user ? 'Edit Admin User' : 'New Admin User'}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column" style={{ height: '100%', maxHeight: '80vh', overflowY: 'auto' }}>
        <Form onSubmit={formik.handleSubmit}>
          <Form.Group className="mb-3" controlId="projectId">
            <Form.Label>Type</Form.Label>
            <FormikSelect
              name="projectId"
              label="Select Project"            
              options={[{ value: 0, label: 'GVE_Admin' }, { value: -1, label: 'GVE_Analyst' }, ...projects.map(project => ({ value: project.id, label: `${project.name} Admin` }))]}
              formik={formik}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="firstName">
            <Form.Label>First Name</Form.Label>
            <Form.Control {...formik.getFieldProps('firstName')} isInvalid={formik.touched.firstName && formik.errors.firstName} />
            <Form.Control.Feedback type="invalid">{formik.errors.firstName}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="lastName">
            <Form.Label>Last Name</Form.Label>
            <Form.Control {...formik.getFieldProps('lastName')} isInvalid={formik.touched.lastName && formik.errors.lastName} />
            <Form.Control.Feedback type="invalid">{formik.errors.lastName}</Form.Control.Feedback>
          </Form.Group>

          {/* Hide username field in edit mode */}
          {!formik.values.isEdit && (
            <Form.Group className="mb-3" controlId="username">
              <Form.Label>Username</Form.Label>
              <Form.Control type="text" value={formik.values.username} readOnly />
            </Form.Group>
          )}

          <Form.Group className="mb-3" controlId="birthDate">
            <Form.Label>Birthdate</Form.Label>
            <Form.Control
              type="date"
              {...formik.getFieldProps('birthDate')}
              isInvalid={formik.touched.birthDate && formik.errors.birthDate}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.birthDate}</Form.Control.Feedback>
          </Form.Group>

          {/* Show password fields only when creating a new user */}
          {!user && (
            <>
              <Form.Group className="mb-3" controlId="password">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  {...formik.getFieldProps('password')}
                  isInvalid={formik.touched.password && formik.errors.password}
                />
                <Form.Control.Feedback type="invalid">{formik.errors.password}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="repeatPassword">
                <Form.Label>Repeat Password</Form.Label>
                <Form.Control
                  type="password"
                  {...formik.getFieldProps('repeatPassword')}
                  isInvalid={formik.touched.repeatPassword && formik.errors.repeatPassword}
                />
                <Form.Control.Feedback type="invalid">{formik.errors.repeatPassword}</Form.Control.Feedback>
              </Form.Group>
            </>
          )}

          <div className="d-flex justify-content-center mt-3">
            <Button type="submit" variant="primary" className="px-5">
              Submit
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditUserModal;
